import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "./useUser";

/**
 * Returns:
 *   hasAccess        — true when the user has a paid subscription with credits
 *   auditsRemaining  — total credits across active subscriptions (0 for admins
 *                      who don't have any)
 *   isAdmin          — true when public.is_admin(uid) returns true. Admins can
 *                      launch Diagnostic Pro without paying — the backend
 *                      start-pro-audit bypasses consume_audit_credit and
 *                      inserts the session with is_admin_session=true +
 *                      subscription_id=null (4319dae, 2026-05-20).
 *
 * The UI in AuditPro.tsx should treat (hasAccess || isAdmin) as
 * "can launch a scan", with a visible "Mode admin (gratuit)" indicator
 * when the user is on the admin path.
 *
 * loading stays true until BOTH hasAccess and isAdmin have resolved so the
 * UI doesn't flash the wrong gate.
 */
export function useProAccess() {
  const { user, loading: authLoading } = useUser();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [audits, setAudits] = useState<number>(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHasAccess(false);
      setIsAdmin(false);
      setAudits(0);
      return;
    }
    let cancelled = false;
    (async () => {
      // Parallel: subscription-based access, audit count, admin flag.
      const [accessRes, adminRes, subsRes] = await Promise.all([
        supabase.rpc("has_pro_audit_available" as any, { uid: user.id }),
        supabase.rpc("is_admin" as any, { uid: user.id }),
        (supabase
          .from("subscriptions" as any) as any)
          .select("audits_remaining")
          .eq("user_id", user.id)
          .eq("status", "active"),
      ]);
      if (cancelled) return;
      setHasAccess(!!accessRes.data);
      setIsAdmin(adminRes.data === true);
      const subs = subsRes.data ?? [];
      const total = subs.reduce(
        (sum: number, s: any) => sum + (s.audits_remaining || 0),
        0,
      );
      setAudits(total);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return {
    hasAccess,
    auditsRemaining: audits,
    isAdmin,
    user,
    loading: authLoading || hasAccess === null || isAdmin === null,
  };
}
